import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Landmark, Check, X, ExternalLink, BadgeDollarSign } from 'lucide-react';
import { SwapStatus } from './SwapForm';
import { money } from './DeskForms';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-desk/admin`;

const Actions = ({ r, onAct }) => {
  const [ref, setRef] = useState('');
  if (r.status === 'pending') return (
    <span className="space-x-1 whitespace-nowrap">
      <button onClick={() => onAct(r.id, 'approve')} className="px-2 py-1 bg-emerald-600 text-white rounded text-xs" data-testid={`desk-approve-${r.id}`}><Check className="w-3 h-3 inline" /> Approve</button>
      <button onClick={() => onAct(r.id, 'reject')} className="px-2 py-1 bg-slate-600 text-white rounded text-xs" data-testid={`desk-reject-${r.id}`}><X className="w-3 h-3 inline" /> Reject</button>
    </span>
  );
  if (r.status === 'awaiting_payout') return (
    <span className="flex gap-1 items-center">
      <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Payout reference" className="px-2 py-1 bg-slate-900 border border-slate-600 rounded text-xs text-white w-32" data-testid={`desk-paid-ref-${r.id}`} />
      <button disabled={ref.trim().length < 3} onClick={() => onAct(r.id, 'paid', { reference: ref })} className="px-2 py-1 bg-violet-600 disabled:opacity-40 text-white rounded text-xs whitespace-nowrap" data-testid={`desk-paid-${r.id}`}><BadgeDollarSign className="w-3 h-3 inline" /> Mark paid</button>
      <button onClick={() => onAct(r.id, 'reject')} className="px-2 py-1 bg-slate-600 text-white rounded text-xs" data-testid={`desk-cancel-${r.id}`}>{r.kind === 'credit_cashout' ? 'Restore credit' : 'Return BETH'}</button>
    </span>
  );
  return r.tx ? <a href={r.tx} target="_blank" rel="noreferrer" className="text-emerald-400 text-xs inline-flex items-center gap-1">tx <ExternalLink className="w-3 h-3" /></a> : null;
};

const Row = ({ r, onAct }) => (
  <tr className="border-t border-slate-700 align-top" data-testid={`desk-row-${r.id}`}>
    <td className="py-2 text-xs">{new Date(r.created_at).toLocaleString()}</td>
    <td className="text-xs">{r.email}</td>
    <td className="capitalize">{r.kind === 'mint' ? 'Buy' : r.kind === 'credit_cashout' ? 'Credit cash-out' : 'Cash out'} · {r.network}</td>
    <td className="text-xs">{r.kind === 'mint' ? `Paid ${money(r.gross)} → mint ${r.net} BETH` : r.kind === 'credit_cashout' ? `Draw ${money(r.gross)} credit → pay ${money(r.net)}` : `${r.gross} BETH → pay ${money(r.net)}`}
      <p className="text-emerald-400">Fee {money(r.fee_usd)}</p></td>
    <td className="text-xs max-w-[220px] break-words">{r.kind === 'mint' ? `${r.payment_method.toUpperCase()}: ${r.payment_reference}` : r.payout_details}</td>
    <td><SwapStatus s={r.status} />{r.error && <p className="text-xs text-red-400 max-w-xs">{r.error}</p>}</td>
    <td><Actions r={r} onAct={onAct} /></td>
  </tr>
);

export const AdminDeskPanel = ({ token }) => {
  const [reqs, setReqs] = useState([]);
  const load = useCallback(() => {
    axios.get(`${API}/requests`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setReqs(r.data.requests)).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!reqs.some(r => r.status === 'executing')) return undefined;
    const t = setInterval(load, 6000); return () => clearInterval(t);
  }, [reqs, load]);

  const act = async (id, kind, body = {}) => {
    try { const r = await axios.post(`${API}/requests/${id}/${kind}`, body, { headers: { Authorization: `Bearer ${token}` } }); toast.success(r.data.message); }
    catch (e) { toast.error(e.response?.data?.detail?.toString() || 'Failed'); }
    load();
  };
  const waiting = reqs.filter(r => ['pending', 'awaiting_payout'].includes(r.status)).length;

  return (
    <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 space-y-4" data-testid="admin-desk-panel">
      <div>
        <h2 className="text-xl font-bold text-white flex items-center gap-2"><Landmark className="w-5 h-5 text-emerald-400" /> Mint &amp; Redeem Desk ({waiting} need you)</h2>
        <p className="text-xs text-slate-400 mt-1">Buy: check the payment arrived, then Approve. It records the net amount as cash reserve and mints it to the subscriber; the fee is yours. Cash out: Approve moves their BETH into escrow; send them the payout, then Mark paid to burn it.</p>
      </div>
      {!reqs.length ? <p className="text-xs text-slate-500" data-testid="desk-empty">No desk requests yet.</p> : (
        <div className="overflow-x-auto"><table className="w-full text-sm text-slate-300">
          <thead><tr className="text-left text-xs text-slate-500"><th>When</th><th>Who</th><th>Type</th><th>Amounts</th><th>Payment / payout</th><th>Status</th><th /></tr></thead>
          <tbody>{reqs.map(r => <Row key={r.id} r={r} onAct={act} />)}</tbody>
        </table></div>
      )}
    </div>
  );
};
